import React, { useEffect, useState } from 'react';
import Shell from '../Shell';
import { api } from '../api';
import { useToast } from '../ToastContext';
import { useAuth } from '../AuthContext';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState({ email: '', name: '', role: 'Facilitator' });
  const [lastInviteUrl, setLastInviteUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();
  const { user: me } = useAuth();

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      setUsers((await api.listUsers()).items);
    } catch (e) {
      setError(e.message || 'Failed to load team');
      toast.push(e.message || 'Failed to load team', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function sendInvite() {
    try {
      const res = await api.inviteUser(invite);
      toast.push(`Invite created for ${invite.email}`);
      setLastInviteUrl(`${window.location.origin}${res.acceptUrl}`);
      setShowInvite(false);
      setInvite({ email: '', name: '', role: 'Facilitator' });
      reload();
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    }
  }

  async function update(id, patch) {
    try {
      await api.patchUser(id, patch);
      reload();
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    }
  }

  return (
    <Shell title="Team" subtitle={`${users.length} member${users.length === 1 ? '' : 's'}`}
      actions={me?.role === 'Admin' && <button className="btn" onClick={() => setShowInvite(true)}>+ Invite teammate</button>}>
      {lastInviteUrl && (
        <div className="banner review" style={{ marginBottom: 16 }}>
          <span>📧</span>
          <div>Share this invite link (Phase 1 — real email delivery wires in Phase 2):<br />
            <code style={{ fontSize: 12, wordBreak: 'break-all' }}>{lastInviteUrl}</code>
          </div>
          <button className="btn btn-sm btn-secondary" onClick={() => { navigator.clipboard?.writeText(lastInviteUrl); toast.push('Copied'); }}>Copy</button>
          <button className="btn btn-sm btn-ghost" onClick={() => setLastInviteUrl(null)}>Dismiss</button>
        </div>
      )}
      {loading && <div className="empty">Loading team…</div>}
      {!loading && error && (
        <div className="banner risk">
          <div style={{ flex: 1 }}>Couldn’t load the team. {error}</div>
          <button className="btn btn-sm btn-secondary" onClick={reload}>Retry</button>
        </div>
      )}
      {!loading && !error && users.length === 0 && (
        <div className="empty">
          <h3>No teammates yet</h3>
          <p>Invite your first teammate to collaborate on RFQs.</p>
          {me?.role === 'Admin' && <button className="btn btn-sm" onClick={() => setShowInvite(true)}>+ Invite teammate</button>}
        </div>
      )}
      {!loading && !error && users.length > 0 && (
      <table className="table">
        <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Active</th><th>Last login</th><th></th></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u._id}>
              <td><b>{u.name}</b></td>
              <td>{u.email}</td>
              <td>
                {me?.role === 'Admin' && String(me.id) !== String(u._id) ? (
                  <select className="select" value={u.role} onChange={(e) => update(u._id, { role: e.target.value })}>
                    {['Facilitator', 'Manager', 'Admin'].map((r) => <option key={r}>{r}</option>)}
                  </select>
                ) : u.role}
              </td>
              <td>
                {u.active
                  ? <span className="chip ready"><span aria-hidden="true">✓</span> Active</span>
                  : <span className="chip muted"><span aria-hidden="true">●</span> Inactive</span>}
                {!u.active && !u.passwordHash && <span className="chip info" style={{ marginLeft: 4 }}><span aria-hidden="true">◐</span> Pending invite</span>}
              </td>
              <td style={{ fontSize: 12, color: 'var(--ink-500)' }}>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}</td>
              <td>
                {me?.role === 'Admin' && String(me.id) !== String(u._id) && (
                  <button className="btn btn-sm btn-ghost" onClick={() => update(u._id, { active: !u.active })}>
                    {u.active ? 'Deactivate' : 'Activate'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      )}

      {showInvite && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,30,0.5)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ width: 440 }}>
            <h2 className="card-title">Invite teammate</h2>
            <label className="field"><span className="label">Name</span>
              <input className="input" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} />
            </label>
            <label className="field"><span className="label">Email</span>
              <input className="input" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
            </label>
            <label className="field"><span className="label">Role</span>
              <select className="select" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })}>
                {['Facilitator', 'Manager', 'Admin'].map((r) => <option key={r}>{r}</option>)}
              </select>
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowInvite(false)}>Cancel</button>
              <button className="btn" onClick={sendInvite} disabled={!invite.email || !invite.name}>Create invite</button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
